import React, { FC, useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { IconChevronDown } from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import Pill from "../Pill";
import { NAVBAR_THEMES, NavbarTheme } from "../../constants/themes";

export type CountriesType = {
  code: string;
  img: string;
};

const COUNTRY_NAMES: Record<string, string> = {
  sv: "El Salvador",
  gt: "Guatemala",
  pa: "Panamá",
};

type Props = {
  mode: NavbarTheme;
  countries: CountriesType[];
  className?: string;
  dropdownAlign?: "left" | "right";
  mobile?: boolean;
};

const CountriesSelect: FC<Props> = ({ mode, countries, className, dropdownAlign = "right", mobile = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { country } = useParams<{ country: string }>();
  const { pathname } = useLocation();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = countries.find((c) => c.code === country) || countries[0];

  const getCountryUrl = (option: string) => {
    const parts = pathname.split("/");

    // Buscar el país actual en cualquier posición del path
    const countryCodes = countries.map(c => c.code);
    const countryIndex = parts.findIndex(part => countryCodes.includes(part));
    const currentCountry = countryIndex >= 0 ? parts[countryIndex] : parts[1];

    const forbiddenKeywords = ["comprar", "alquiler", "alquilar"];
    const hasForbiddenKeyword = forbiddenKeywords.some((keyword) =>
      pathname.includes(keyword)
    );

    // SV tiene rutas de comprar/alquiler que no existen en PA/GT — redirigir al home del país
    if (
      currentCountry === "sv" &&
      (option === "pa" || option === "gt") &&
      hasForbiddenKeyword
    ) {
      return `/${option}`;
    }

    // Cambia el país en la posición donde se encontró
    if (countryIndex >= 0) {
      parts[countryIndex] = option;
    } else {
      parts[1] = option; // fallback a la posición 1
    }

    // Truncar la URL hasta el segmento de tipo de propiedad para evitar filtros inválidos entre países
    const keywords = ["propiedades", "casas", "apartamentos", "proyectos"];
    for (let i = 0; i < parts.length; i++) {
      if (keywords.includes(parts[i])) {
        return parts.slice(0, i + 1).join("/");
      }
    }

    return parts.join("/");
  };

  if (mobile) {
    return (
      <div ref={ref}>
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          className="flex items-center gap-2 p-0"
        >
          {selected?.img && (
            <img src={selected.img} className="w-5 h-5 rounded-full object-cover" alt={country} />
          )}
          <span className="!font-grotesk text-white text-base font-medium">{COUNTRY_NAMES[country ?? ""] ?? country?.toUpperCase()}</span>
          <IconChevronDown
            size={14}
            className={`text-white transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.ul
              className="pt-2 pb-6 flex flex-col gap-5 overflow-hidden"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
            >
              {countries.map(({ code, img }) => (
                <li key={code}>
                  <a
                    href={getCountryUrl(code)}
                    className="flex items-center gap-2 text-white text-base hover:text-white transition-colors"
                  >
                    <img src={img} className="w-5 h-5 rounded-full object-cover" alt={code} />
                    <span className="!font-grotesk">{COUNTRY_NAMES[code] ?? code.toUpperCase()}</span>
                  </a>
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <Pill mode={mode} onClick={() => setOpen(!open)} className={className}>
        {selected?.img && (
          <img src={selected.img} className="w-5 h-5 rounded-full object-cover" alt={country} />
        )}
        <span className="!font-grotesk">{COUNTRY_NAMES[country ?? ""] ?? country?.toUpperCase()}</span>
        <IconChevronDown size={14} />
      </Pill>
      {open && (
        <div className={`absolute top-full mt-2 rounded-lg shadow-lg p-2 z-[60] min-w-[120px] ${dropdownAlign === "left" ? "left-0" : "right-0"} ${NAVBAR_THEMES[mode].dropdownBg}`}>
          {countries.map(({ code, img }) => (
            <a
              key={code}
              href={getCountryUrl(code)}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors ${NAVBAR_THEMES[mode].dropdownText} ${NAVBAR_THEMES[mode].dropdownHover}`}
            >
              <img src={img} className="w-6 rounded-[4px]" alt={code} />
              <span>{COUNTRY_NAMES[code] ?? code.toUpperCase()}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default CountriesSelect;
